import React, { useEffect, useState } from 'react';
import { X, Plus, CheckCircle2, ChevronLeft } from 'lucide-react';
import { Client } from '../../types/client.types';
import './ShippingModal.css';

interface ShippingModalProps {
  client: Client;
  onClose: () => void;
  /** Direccion confirmada, en dos lineas: calle y "CP - Ciudad". */
  onConfirm: (address: string) => void;
  /**
   * Si se pasa, aparece la casilla "definir como predeterminada" y se llama
   * con la direccion al confirmar. OJO: el backend no tiene direccion de
   * envio, asi que guardarla PISA la direccion fiscal del cliente. Sin este
   * callback la direccion vive solo en la venta en curso.
   */
  onSaveAsDefault?: (address: string) => void;
}

export function ShippingModal({ client, onClose, onConfirm, onSaveAsDefault }: ShippingModalProps) {
  const [step, setStep] = useState<'choice' | 'form'>('choice');
  const [addr, setAddr] = useState('');
  const [city, setCity] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);

  /**
   * La ficha solo trae `address`. Se guarda y se muestra en dos lineas, igual
   * que la compone este modal al confirmar.
   */
  const registered = client.address || '';

  // `addr` queda fuera de las dependencias a proposito: si entrara, borrar el
  // campo a mano lo volveria a rellenar en la siguiente pasada.
  useEffect(() => {
    if (step === 'form' && registered && !addr) {
      const parts = registered.split('\n');
      setAddr(parts[0] || '');
      setCity(parts[1] || '');
    }
  }, [step, registered]);

  const confirm = (address: string, asDefault: boolean) => {
    onConfirm(address);
    if (asDefault && onSaveAsDefault) onSaveAsDefault(address);
  };

  return (
    <div className="sm-overlay">
      <div className="sm-modal">
        <div className="sm-header">
          <div>
            <h3 className="sm-title">Dirección de Envío</h3>
            <p className="sm-subtitle">Cliente: {client.companyName}</p>
          </div>
          <button onClick={onClose} className="sm-close" title="Cerrar">
            <X style={{ width: '1.5rem', height: '1.5rem' }} />
          </button>
        </div>

        {step === 'choice' ? (
          <div className="sm-choice sm-step-choice">
            <button
              onClick={() => registered && confirm(registered, false)}
              disabled={!registered}
              className="sm-card"
            >
              <div className="sm-card-head">
                <span className="sm-card-label">Dirección Registrada</span>
                {registered && <CheckCircle2 className="sm-card-check" />}
              </div>
              <div style={{ minWidth: 0 }}>
                {registered ? (
                  registered.split('\n').map((line, i) => (
                    <p
                      key={i}
                      className={`sm-addr-line ${i === 0 ? 'sm-addr-line--main' : 'sm-addr-line--sub'}`}
                    >
                      {line}
                    </p>
                  ))
                ) : (
                  <p className="sm-addr-empty">No hay dirección registrada</p>
                )}
              </div>
            </button>

            <button onClick={() => setStep('form')} className="sm-card sm-card--new">
              <div className="sm-card-icon">
                <Plus style={{ width: '1.5rem', height: '1.5rem' }} />
              </div>
              <span className="sm-card-text">Utilizar una dirección diferente</span>
            </button>
          </div>
        ) : (
          <div className="sm-form sm-step-form">
            <div className="sm-fields">
              <div className="sm-field">
                <label className="sm-label">Dirección (Calle, Número, Bloque...)</label>
                <input
                  autoFocus
                  type="text"
                  className="sm-input"
                  placeholder="Ej: Calle Mayor 123, 2ºB"
                  value={addr}
                  onChange={(e) => setAddr(e.target.value)}
                />
              </div>
              <div className="sm-field">
                <label className="sm-label">C.P. y Ciudad</label>
                <input
                  type="text"
                  className="sm-input"
                  placeholder="Ej: 28001 - Madrid"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </div>
            </div>

            {onSaveAsDefault && (
              <div className="sm-default" onClick={() => setSaveAsDefault(!saveAsDefault)}>
                <div className={`sm-checkbox${saveAsDefault ? ' sm-checkbox--on' : ''}`}>
                  {saveAsDefault && <CheckCircle2 style={{ width: '1.25rem', height: '1.25rem' }} />}
                </div>
                <div>
                  <p className="sm-default-title">Definir como dirección predeterminada</p>
                  <p className="sm-default-hint">
                    Se guardará esta dirección en la ficha del cliente para futuros envíos.
                  </p>
                </div>
              </div>
            )}

            <div className="sm-actions">
              <button onClick={() => setStep('choice')} className="sm-btn-back">
                <ChevronLeft style={{ width: '1.25rem', height: '1.25rem' }} />
                VOLVER
              </button>
              <button
                disabled={!addr.trim() || !city.trim()}
                onClick={() => confirm(`${addr.trim()}\n${city.trim()}`, saveAsDefault)}
                className="sm-btn-confirm"
              >
                CONFIRMAR DIRECCIÓN
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
